import { useEffect, useRef, useState } from 'react'
import './App.css'
import { DataProvider } from './components/DataProvider/DataProvider'
import SRPForm from './components/Form/SRPForm'
import { ecucCataSelectObjTemplate } from './models/ECUCInterfaces'
import { getInitializedEcuCataObj } from './utilities/ecuCatalogeUtils'
import APIService from './APIs/APIService'
import API from './APIs/API'
import { SelectOption, setSupplierFieldOptions } from './utilities/dropDownOptions'
import BuildsLoader from './BuildsLoader'
import { silentUserInfoRequest } from './auth/authConfig'
import { useMsal } from '@azure/msal-react'
import { HeaderContainerDiv, WarningBannerDiv } from './components/styledComponents/Components.styles'
import HomeIcon from '@mui/icons-material/Home';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import palette from './utilities/palette'
import { initializeFromStorage, isValidString, isValidObject, isValidArray } from './utilities/persistOnPrefreshUtils'
import { sanitizePublicUrl } from './utilities/utilityFunctions'
import { dynamicSelectOptionsMapTemplate } from './models/DynamicSelectOptionsMapTemplate'


function App() {
    const api = new API(useMsal().instance);
    const apiService = new APIService(api);
    const [schema, setSchema] = useState<any>(null);
    const [ecuCataSelectObj, setECUCataSelectObj] = useState(structuredClone(ecucCataSelectObjTemplate));
    const bypassLoading = false;
    const [isDelayComplete, setIsDelayComplete] = useState(false);
    const [dynamicSelectOptionsMap, setDynamicSelectOptionsMap] = useState<Record<string, SelectOption[]>>(() => 
        initializeFromStorage("dynamicSelectOptionsMap", structuredClone(dynamicSelectOptionsMapTemplate), isValidObject)
    );
    const renderCount = useRef(0);
    const revision = 3;
    const { instance } = useMsal();
    const [formProcessingState, setFormProcessingState] = useState<string>(() => 
        initializeFromStorage("formProcessingState", "ECUC_SELECT", isValidString)
    );
    const [signingAllowedModules, setSigningAllowedModules] = useState<string[]>(() =>
        initializeFromStorage("signingAllowedModules", [], isValidArray)
    );

    // Profile menu avatar state
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        renderCount.current += 1;
        console.log("App Render count", renderCount.current)
    });

    async function fetchLatestSRPSchema(){
        if (bypassLoading) {
            setSchema({bypass:true}); 
            return;
        }
    
        const schemaResponse  = await api.fetch('/srp/schema/latest');

        if (!schemaResponse.ok || schemaResponse.status !== 200) {
            console.warn("ERROR WHEN FETCHING SCHEMA: " + schemaResponse.statusText);
            setSchema({isError:true, message: schemaResponse.statusText});
            return;
        }
        const schemaJson = await schemaResponse.json();

        // Replace https with http in $schema value
        if (!schemaJson?.$schema) {
            console.warn(
                "Schema JSON is missing $schema property"
            );
            setSchema({isError:true, message: "Schema JSON is missing $schema property"});
            return;
        }
        schemaJson.$schema = schemaJson.$schema.replace('https://', 'http://');
        console.log(`Schema Json`, schemaJson);

        setSchema(schemaJson);
    }

    async function fetchFeatureFlags() {
        const response = await apiService.getFeatureFlags();

        if (response.isSuccess && Array.isArray(response.data?.signingAllowedModules)) {
            console.log("App initialized with feature flags:", response.data);
            setSigningAllowedModules(response.data.signingAllowedModules);
            return;
        }

        setSigningAllowedModules([]);
    }

    async function getInitializedData() {
        console.log("Initializing App data...");
        console.log("Revision: ", revision);
        await Promise.all([
            getInitializedEcuCataObj({bypassLoading ,setECUCataSelectObj, apiService}),
            fetchLatestSRPSchema(),
            setSupplierFieldOptions({setDynamicSelectOptionsMap, api}),
            fetchFeatureFlags(),
        ]);


        setTimeout(() => {
            setIsDelayComplete(true);
        }, 1000);

    }


    useEffect(() => {
        getInitializedData();
        getUserInfo();
        console.log("App initialized");
    }, [])

    const [avatar, setAvatar] = useState<string>("");
    async function getUserInfo(){
        const temp = await instance.acquireTokenSilent(silentUserInfoRequest)
        const token = temp.accessToken;

        const url = "https://graph.microsoft.com/v1.0/me/photos/64x64/$value"
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': `image/jpg`
            },
        })

        if(response.ok){
            const url = window.URL || window.webkitURL;
            const blobUrl = url.createObjectURL(await response.blob());
            setAvatar(blobUrl)
        }
    }

    return (
        <>            
            <HeaderContainerDiv style={{fontFamily: 'FordF-1-Semibold', fontSize: '30px', display:'flex', alignItems:'center', justifyContent:'space-between', height: '15vh'}}>
                <span style={{display:'flex', alignItems:'center', gap:'12px'}}>
                    <img src={`${sanitizePublicUrl(process.env.PUBLIC_URL)}/fordMiniLogo.png`} alt='Ford Logo' style={{height:'48px', border:'1px solid white', borderRadius: '50%'}} />
                    <span>SRP Software Release</span>
                </span>

                {/* portrait and logout button go here */}
                <span style={{display:'flex', flexDirection:'row', alignItems:'center', width:'20%', justifyContent:'end', gap:'10px'}}>
                    {
                    (schema && ecuCataSelectObj && isDelayComplete) && 
                    <>
                        {formProcessingState === "CLUSTER_SELECT" &&
                            <Tooltip title="Go to Home" >
                                <IconButton
                                    onClick={() => {
                                        localStorage.clear();
                                        setECUCataSelectObj(structuredClone(ecucCataSelectObjTemplate));
                                        setFormProcessingState("ECUC_SELECT");
                                        setSigningAllowedModules([]);
                                    }}
                                >
                                    <HomeIcon sx={{
                                        fontSize: '48px',
                                        color: palette.main,
                                        ":hover": {
                                            color: palette.hover
                                        }
                                    }} />

                                </IconButton>
                            </Tooltip>
                        }
                        <Tooltip title={open ? "" : "Profile"}>
                            <div>
                                <IconButton 
                                    sx={{
                                        borderRadius: '50%',
                                        padding: '0px',
                                        border: `2px solid ${palette.main}`,
                                        transition: 'box-shadow 0.2s, border-color 0.2s',
                                        '&:hover': {
                                        boxShadow: '0 0 0 4px rgba(0,9,91,0.15)',
                                        borderColor: palette.hover, // or any color you want on hover
                                        },
                                    }}
                                    onClick={handleClick}
                                    >
                                    <img
                                        alt='my-user-avatar'
                                        height='48px'
                                        width='48px'
                                        style={{borderRadius:'50%', borderStyle:'solid', borderWidth:'1px'}}
                                        src={avatar}
                                        loading="lazy"
                                        decoding="async"
                                        referrerPolicy="no-referrer"
                                        crossOrigin="anonymous"
                                    />
                                </IconButton>
                                <Menu
                                id="basic-menu"
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}   
                                slotProps={{
                                    list: {
                                    'aria-labelledby': 'basic-button',
                                    },
                                }}
                                sx={{mt: '10px'}}
                                >
                                <MenuItem 
                                    onClick={() => {
                                        instance.logoutRedirect();
                                    }}
                                >Log out</MenuItem>

                                </Menu>
                            </div>     
                        </Tooltip>   

                    </>
                    }
                </span>
            </HeaderContainerDiv>
                {
                (schema && ecuCataSelectObj && isDelayComplete) ? 
                
                    <DataProvider
                        api={api}
                        apiService={apiService}
                        schema={schema} setSchema={setSchema}
                        ecuCataSelectObj={ecuCataSelectObj} setECUCataSelectObj={setECUCataSelectObj}
                        dynamicSelectOptionsMap={dynamicSelectOptionsMap} setDynamicSelectOptionsMap={setDynamicSelectOptionsMap} 
                        formProcessingState={formProcessingState} setFormProcessingState={setFormProcessingState}
                        signingAllowedModules={signingAllowedModules}
                        setSigningAllowedModules={setSigningAllowedModules}
                    >
                        <SRPForm />

                    </DataProvider>
                    : 
                    <BuildsLoader
                        schema={schema}
                        ecuCataSelectObj={ecuCataSelectObj}
                        dynamicSelectOptionsMap={dynamicSelectOptionsMap}
                    />
                }
        </>
    )
}

export default App
